import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';

import { getFilesBlockId } from 'hb-react/frontoffice/apps/MediaCenter/container/utils/rendererUtils';
import { useMediaCenterCategoriesContext } from 'hb-react/frontoffice/apps/MediaCenter/contexts/MediaCenterCategoriesContext';
import { useMediaCenterPathContext } from 'hb-react/frontoffice/apps/MediaCenter/contexts/MediaCenterPathContext';
import { useStore } from 'hb-react/frontoffice/components/BlocksRenderer/store';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useFilePreviewNavigation } from 'hb-react/shared/components/FilePreview/hooks/useFilePreviewNavigation';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import { hasMoreThanOneModalOpen } from 'hb-react/shared/utils/modalception';

import { useFilePreview } from './hooks/useFilePreview';
import { useLikesAndComments } from './hooks/useLikesAndComments';
import { usePrefetchLikesAndComments } from './hooks/usePrefetchLikesAndComments';
import LoadingOrNotFound from './LoadingOrNotFound';
import MediaCenterFilePreview from './MediaCenterFilePreview';

const dataSelector = (state) => state.data;

const MediaCenterPreviewModal = ({ folderUuid, fetchNextPage, topicName }) => {
  const { currentNetwork } = useDefaultAppContexts();
  const data = useStore(dataSelector);

  const filesId = getFilesBlockId(folderUuid);
  const files = data?.[filesId];

  const { filePreview, fileNotFound, isLoading } = useFilePreview(
    files,
    currentNetwork.id,
  );

  if (isLoading || fileNotFound) {
    return <LoadingOrNotFound fileNotFound={fileNotFound} />;
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: filePreview?.name,
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName,
        })}
      />
      <MediaCenterFilePreviewContainer
        filePreview={filePreview}
        fetchNextPage={fetchNextPage}
        files={files}
        folderUuid={folderUuid}
      />
    </>
  );
};

const MediaCenterFilePreviewContainer = ({
  files,
  filePreview,
  folderUuid,
  fetchNextPage,
}) => {
  const { mediaCenterFolderPath, mediaCenterFilePath } =
    useMediaCenterPathContext();
  const queryClient = useQueryClient();
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const navigate = useNavigate();
  const { search } = window.location;
  const { categories } = useMediaCenterCategoriesContext();

  const showReactions = isCommentsModuleAvailable(
    currentUser,
    !currentNetwork?.network_configuration?.media_center?.comments_enabled,
  );

  const { id, parent_uuid: parentUuid, page: filePreviewPage } = filePreview;

  const closePreviewUrl = mediaCenterFolderPath(folderUuid);

  const {
    hasPagination,
    handleKeyNavigation,
    previousUrl,
    nextUrl,
    nextFile,
    onSwipedLeft,
    onSwipedRight,
  } = useFilePreviewNavigation({
    files,
    closePreviewUrl,
    getFileUrl: (uuid) => mediaCenterFilePath(uuid),
    search,
  });

  const {
    likes,
    isLoadingLikes,
    comments,
    isLoadingComments,
    onLikeCallback,
    onCommentCallback,
    totalComments,
  } = useLikesAndComments({
    fileId: id,
    showReactions,
  });

  usePrefetchLikesAndComments({
    fileId: nextFile?.id,
    showReactions,
  });

  useEffect(() => {
    if (hasPagination && !nextUrl && fetchNextPage) {
      fetchNextPage();
    }
  }, [hasPagination, nextUrl, fetchNextPage]);

  const onCloseModal = () => {
    const filesId = getFilesBlockId(folderUuid);

    if (filePreviewPage) {
      queryClient.invalidateQueries({
        queryKey: filesId,
        refetchPage: ({ page }) => page === filePreviewPage,
      });
    }

    if (hasMoreThanOneModalOpen()) return;

    navigate(closePreviewUrl + search);
  };

  const folderUrl =
    parentUuid !== 'root' ? mediaCenterFolderPath(parentUuid) : null;

  return (
    <MediaCenterFilePreview
      categories={categories}
      comments={comments}
      filePreview={filePreview}
      folderUrl={folderUrl}
      handleKeyNavigation={handleKeyNavigation}
      hasPagination={hasPagination}
      isLoadingComments={isLoadingComments}
      isLoadingLikes={isLoadingLikes}
      likes={likes}
      nextUrl={nextUrl}
      onCloseModal={onCloseModal}
      onCommentCallback={onCommentCallback}
      onLikeCallback={onLikeCallback}
      onSwipedLeft={onSwipedLeft}
      onSwipedRight={onSwipedRight}
      previousUrl={previousUrl}
      search={search}
      showReactions={showReactions}
      totalComments={totalComments}
    />
  );
};

export default MediaCenterPreviewModal;
